import Icon from '@/components/Icon';
import Botao from '../../components/Botao';
import styles from './TituloChat.module.css'
import { mdiBellCancel, mdiBellRing, mdiCheckCircle, mdiClose, mdiFilter, mdiFilterCheck, mdiFilterOutline, mdiHeadset, mdiLanDisconnect, mdiLanPending, mdiMagnify, mdiMessageAlert, mdiMessagePlus, mdiMessageProcessing, mdiPhone, mdiReload, mdiRobot, mdiTrashCan, mdiVolumeOff } from '@mdi/js';
import BotaoDropdown from '@/components/BotaoDropdown';
import Grid, { GridItem } from '@/components/layout/Grid';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoData from '@/components/Formulario/CampoData';
import Tabs from '@/components/layout/Tabs';
import { useQuery } from 'react-query';
import axios from 'axios';
import HBox from '@/components/layout/HBox';
import IconeCanal from '@/components/layout/IconeCanal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useMemo } from 'react';
import { set } from 'date-fns';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import AudioDevicesIndicator from '@/components/AudioDevicesIndicator';
import { useAuth } from '../../lib/useAuth';
import { FiltroAtendente } from '@/components/Filtros/FiltroAtendente';
import FiltroGrupoUsuario from '@/components/Filtros/FiltroGrupoUsuario';

type Query = {
    search?: string;
    canalId?: number;
    userId?: number;
    chatStatus?: string;
    dataInicio?: string;
    dataFim?: string;
    chatTags?: string[];
    mode?: 'related' | 'recent';
    apenasMeus?: boolean;
    usuarioResponsavelId?: string[];
    grupoResponsavelId?: string[];
}

type TituloChatProps = {
    wsState: 'disconnected' | 'connecting' | 'connected' | 'error',
    loading: boolean,
    onNewChat: () => void,
    onReloadChats: (options?: any, force?: boolean, preservarSelecao?: boolean) => void
    notificationStatus?: NotificationPermission
    onAskPermission?: () => void;
    query: Query,
    setQuery: (query: Query) => void,
    resetQuery: () => void,
    isFiltered: boolean,
    hasNewMessages: boolean,
    canHandleCalls: boolean;
    callsWarning: string | null;
    onOpenDialer?: () => void;
    hasVoipOutbound?: boolean;
}

type QueryFilterProps = {
    query: Query,
    setQuery: (query: Query) => void,
    resetQuery: () => void,
    isFiltered: boolean,
    loadingChats: boolean,
}
const QueryFilter = ({ query, setQuery, resetQuery, isFiltered, loadingChats }: QueryFilterProps) => {
    const navigate = useNavigate();
    const [urlSearch] = useSearchParams();
    const uid = urlSearch.get('uid');
    const { user } = useAuth();

    const [internalQuery, setInternalQuery] = React.useState<Query>(query);

    // Sincroniza com filtros aplicados fora do formulário (ex.: botão "Limpar"),
    // senão o painel guardaria seleções antigas e "Limpar" não zeraria os seletores.
    React.useEffect(() => {
        setInternalQuery(query);
    }, [query]);

    const queryCanais = useQuery(['canal.box'], async () => {
        const { data } = await axios.get('/api/canal/box');
        return data;
    });

    const queryClientes = useQuery(['cliente', uid], async () => {
        if (!uid) return;

        const { data } = await axios.get('/api/cliente/' + uid);
        return data;
    });
    const nomeCliente = queryClientes.data?.result?.nome;

    const canais = [
        {
            id: '',
            label: <span style={{ paddingLeft: '22px' }}>Todos os canais</span>,
        },
    ];
    if (queryCanais.isSuccess) {
        canais.push(
            ...queryCanais.data.result
                .map((c: any) => ({
                    id: c.id,
                    sort: c.nome,
                    label: (
                        <HBox gap="8px">
                            <IconeCanal tipo={c.tipo} style={{ height: '14px' }} />
                            {c.nome}
                        </HBox>
                    ),
                }))
                .sort((a: any, b: any) => a.sort.localeCompare(b.sort)),
        );
    }

    const queryMarcadores = useQuery(['chat.marcadores'], async () => {
        const { data } = await axios.get('/api/chat/marcadores?dataIni=' + encodeURIComponent(query.dataInicio || '') + '&dataFim=' + encodeURIComponent(query.dataFim || ''));
        return data;
    });
    const marcadoresOptions = queryMarcadores.data?.result.map((m: string) => ({ id: m, label: m })) || [];

    // Ícone muda se houver filtro ativo
    const filterIcon = isFiltered ? mdiFilterCheck : mdiFilterOutline;

    // Filtro por atendente/grupo responsável: só para GESTOR/ADMIN, e só para
    // GESTOR quando ele estiver em mais de um grupo (senão o filtro é óbvio).
    const showResponsavelFilters = user?.papel === 'ADMIN' || (user?.papel === 'GESTOR' && (user?.grupos?.length ?? 0) > 1);

    return (
        <BotaoDropdown icon={filterIcon} variant='none-primary' dropdownStyle={{ width: '320px' }} >
            {({ closeDropdown }) => (
                <form onSubmit={e => { e.preventDefault(); setQuery(internalQuery); }}>
                    <Grid>
                        <GridItem sm={12}>
                            <CampoTexto
                                prepend={
                                    <Icon
                                        path={mdiMagnify}
                                        size={1}
                                        color="var(--tc-color-gray-500)"
                                    />
                                }
                                placeholder="Pesquisar"
                                value={internalQuery.search ?? ''}
                                onChange={v => { setInternalQuery({ ...internalQuery, search: v }) }}
                                disabled={loadingChats}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoCheckbox
                                value={internalQuery.apenasMeus ?? false}
                                onChange={v => {
                                    // "Apenas meus" e o seletor de atendente/grupo são
                                    // mutuamente exclusivos: o último tocado vence, senão
                                    // as duas restrições juntas zeram a lista.
                                    setInternalQuery({
                                        ...internalQuery,
                                        apenasMeus: v || undefined,
                                        ...(v ? { usuarioResponsavelId: undefined, grupoResponsavelId: undefined } : {}),
                                    })
                                }}
                                disabled={loadingChats}
                            >
                                Apenas meus atendimentos
                            </CampoCheckbox>
                        </GridItem>
                        {user?.papel === 'GESTOR' && (
                            <GridItem sm={12}>
                                <CampoSeletor
                                    noSearch
                                    label='Atendimentos'
                                    disabled={loadingChats}
                                    placeholder="Apenas recentes"
                                    options={[
                                        { id: 'recent', label: 'Apenas recentes' },
                                        { id: 'related', label: 'Com participação' }
                                    ]}
                                    value={internalQuery.mode ?? 'recent'}
                                    onChange={v => { setInternalQuery({ ...internalQuery, mode: v as 'related' | 'recent' || 'recent' }) }}
                                />
                            </GridItem>
                        )}
                        {showResponsavelFilters && (
                            <GridItem sm={12}>
                                <FiltroAtendente
                                    value={internalQuery.usuarioResponsavelId ?? []}
                                    onChange={v => {
                                        setInternalQuery({
                                            ...internalQuery,
                                            usuarioResponsavelId: v.length > 0 ? v : undefined,
                                            ...(v.length > 0 ? { apenasMeus: undefined } : {}),
                                        })
                                    }}
                                />
                            </GridItem>
                        )}
                        {showResponsavelFilters && (
                            <GridItem sm={12}>
                                <FiltroGrupoUsuario
                                    value={internalQuery.grupoResponsavelId ?? []}
                                    onChange={v => {
                                        setInternalQuery({
                                            ...internalQuery,
                                            grupoResponsavelId: v.length > 0 ? v : undefined,
                                            ...(v.length > 0 ? { apenasMeus: undefined } : {}),
                                        })
                                    }}
                                />
                            </GridItem>
                        )}
                        <GridItem sm={12}>
                            <CampoSeletor
                                label='Canais'
                                disabled={loadingChats}
                                placeholder="Todos os canais"
                                options={canais}
                                value={internalQuery.canalId?.toString() ?? ''}
                                onChange={v => { setInternalQuery({ ...internalQuery, canalId: +v! || undefined }) }}
                            />
                        </GridItem>
                        {nomeCliente && (
                            <GridItem sm={12}>
                                <HBox
                                    stretch
                                    style={{
                                        border: '2px dashed var(--tc-color-gray-300)',
                                        backgroundColor: 'var(--tc-color-gray-100)',
                                        borderRadius: '12px',
                                        padding: '4px 8px',
                                    }}
                                >
                                    <span>{nomeCliente}</span>
                                    <Botao
                                        variant="none-primary"
                                        icon={mdiTrashCan}
                                        onClick={() => {
                                            setInternalQuery({ ...internalQuery, userId: undefined });
                                            navigate('/chat', { replace: true });
                                        }}
                                    />
                                </HBox>
                            </GridItem>
                        )}
                        <GridItem sm={12}>
                            <CampoData
                                label='Data início'
                                type='datetime-local'
                                disabled={loadingChats}
                                value={internalQuery.dataInicio ? internalQuery.dataInicio : undefined}
                                onChange={(v) => setInternalQuery({ ...internalQuery, dataInicio: v })}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoData
                                label='Data fim'
                                type='datetime-local'
                                disabled={loadingChats}
                                value={internalQuery.dataFim ? internalQuery.dataFim : undefined}
                                onChange={(v) => setInternalQuery({ ...internalQuery, dataFim: v })}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <CampoMultiSeletor
                                label='Marcadores'
                                disabled={loadingChats}
                                placeholder="Todos os marcadores"
                                options={marcadoresOptions}
                                value={internalQuery.chatTags ? internalQuery.chatTags : []}
                                onChange={v => { setInternalQuery({ ...internalQuery, chatTags: v.length > 0 ? v : undefined }) }}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <Tabs
                                className={styles.tabs}
                                selectedTab={internalQuery.chatStatus}
                                disabled={loadingChats}
                                tabs={[
                                    { id: '', label: 'Todos', icon: mdiMessageProcessing },
                                    { id: 'chatbot', label: 'Chatbots', icon: mdiRobot },
                                    { id: 'atendimento', label: 'Atendimento', icon: mdiHeadset },
                                    {
                                        id: 'finalizado',
                                        label: 'Finalizados',
                                        icon: mdiCheckCircle,
                                    },
                                ]}
                                onTabChange={tab => { setInternalQuery({ ...internalQuery, chatStatus: tab as any }) }}
                            />
                        </GridItem>
                        <GridItem sm={12}>
                            <HBox gap="8px">
                                <Botao
                                    variant='secondary'
                                    onClick={() => {
                                        resetQuery();
                                        closeDropdown();
                                    }}
                                    disabled={loadingChats}
                                    icon={mdiClose}
                                >
                                    Limpar
                                </Botao>
                                <Botao
                                    variant='primary'
                                    onClick={() => {
                                        setQuery(internalQuery);
                                    }}
                                    disabled={loadingChats}
                                    icon={mdiFilter}
                                >
                                    Aplicar
                                </Botao>
                            </HBox>
                        </GridItem>
                    </Grid>
                </form>
            )}
        </BotaoDropdown>
    )
}

const TituloChat = ({ wsState, loading, notificationStatus, onNewChat, onAskPermission, onReloadChats, query, setQuery, resetQuery, isFiltered, hasNewMessages, canHandleCalls, callsWarning, onOpenDialer, hasVoipOutbound }: TituloChatProps) => {
    // Verificar se há canais com ligações habilitadas
    const queryCanais = useQuery(['canal.box'], async () => {
        const { data } = await axios.get('/api/canal/box');
        return data;
    });

    const hasCallingEnabled = useMemo(() => {
        if (!queryCanais.data?.result) return false;

        // O backend agora retorna callingEnabled diretamente em cada canal
        return queryCanais.data.result.some((canal: any) => canal.callingEnabled);
    }, [queryCanais.data?.result]);

    return (
        <div className={styles.container}>
            <div className={styles.titulo}>
                Chats
                <Botao variant="none-primary" icon={mdiMessagePlus} title="Nova conversa" onClick={onNewChat} />
                {hasVoipOutbound && (
                    <Botao
                        variant="none-success"
                        icon={mdiPhone}
                        title="Realizar ligação"
                        onClick={onOpenDialer}
                    />
                )}
            </div>

            {hasCallingEnabled && (
                <AudioDevicesIndicator
                    canHandleCalls={canHandleCalls}
                    warning={callsWarning}
                />
            )}

            {/*wsState === 'connected' && <Botao variant="none-primary" icon={mdiLanCheck} />*/}
            {wsState === 'error' && <Icon path={mdiLanDisconnect} size={1.25} color="var(--tc-color-danger)" title="Erro ao tentar conectar ao servidor" />}
            {wsState === 'connecting' && <Icon path={mdiLanPending} size={1.25} color="var(--tc-color-warning)" title="Conectando ao servidor..." />}
            {wsState === 'disconnected' && <Icon path={mdiLanDisconnect} size={1.25} color="var(--tc-color-warning)" title="Desconectado" />}

            {notificationStatus === 'default' && <Botao variant="none-primary" icon={mdiBellRing} onClick={onAskPermission} />}
            {/* {notificationStatus === 'granted' && <Botao variant="none-primary" icon={mdiBellCancel} onClick={onRemovePermission} />} */}

            {hasNewMessages && <Icon path={mdiMessageAlert} size={1.25} color="var(--tc-color-warning)" title="Existem novas mensagens em chats. Limpe os filtros para ver." className="blink" />}

            <QueryFilter query={query} setQuery={setQuery} resetQuery={resetQuery} isFiltered={isFiltered} loadingChats={loading} />
            {/* `query` é obrigatório: sem ele o servidor recarrega sem filtro e o
                cliente descarta o que não casa, encurtando a lista e acendendo o
                aviso de "novas mensagens". O terceiro argumento mantém aberta a
                conversa que estiver fora da primeira página. */}
            <Botao variant="none-primary" icon={mdiReload} disabled={loading} iconProps={{ spin: loading }} onClick={() => onReloadChats(query, false, true)} />
        </div>
    )
}

export default TituloChat;